import { useBoolean } from 'ahooks'
import { Card, Form as AntForm, FormProps as AntFormProps, Skeleton, Button, message } from 'antd'
import { FormInstance } from 'antd/es/form/Form'
import React, { useEffect, useImperativeHandle } from 'react'
import { registerComponentProperty } from '../util/util'
import { post, put, patch, destroy } from '@rails/request.js'
import FormItem from './FormItem'
import { FormItemType } from './consts'

declare type RecursivePartial<T> = T extends object
  ? {
      [P in keyof T]?: T[P] extends (infer U)[]
        ? RecursivePartial<U>[]
        : T[P] extends object
        ? RecursivePartial<T[P]>
        : T[P]
    }
  : any

export type FormType<Values = any> = {
  form: FormInstance<Values>
  onSubmit: () => void
  resetFields: () => void
  setFieldsValue: FormInstance<Values>['setFieldsValue']
  getFieldsValue: FormInstance<Values>['getFieldsValue']
}

export interface FormProps<Values = any> extends AntFormProps<Values> {
  data?: RecursivePartial<Values>
  formData?: any[]
  submit?:
    | ((values: Values) => any)
    | ((values: Values) => Promise<void>)
  cancel?: () => void
  title?: string
  card?: boolean
  showActionButtons?: boolean
  loading?: boolean
  wrapperClassNames?: string
  submitPath?: string,
  submitMethod?: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  submitSuccess?: (e: any) => void
}

const Form = <Values = any,>(
  {
    data,
    formData = [],
    submit,
    cancel,
    title,
    card = true,
    showActionButtons = true,
    loading = false,
    wrapperClassNames,
    submitPath,
    submitMethod,
    submitSuccess,
    ...formProps
  }: FormProps<any>,
  ref: React.Ref<FormType<Values>> | null | undefined
) => {
  const form = formProps.form ?? AntForm.useForm()[0]
  
  const [submitLoading, submitLoadingActions] = useBoolean(false)

  useImperativeHandle(ref, () => ({
    form,
    onSubmit: () => {
      form?.submit()
    },
    resetFields: () => {
      form?.resetFields()
    },
    setFieldsValue: values => {
      form?.setFieldsValue(values)
    },
    getFieldsValue: () => form?.getFieldsValue(),
  }))

  const onFinishFailed: FormProps<Values>['onFinishFailed'] = errorInfo => {
    console.log('Failed:', errorInfo)

    const firstErrorField = errorInfo?.errorFields?.find((field) => field?.errors?.length)
    const firstErrorMessage = firstErrorField?.errors?.[0]

    if (firstErrorMessage) {
      message.error(firstErrorMessage)
    } else {
      message.error('请检查表单填写是否完整')
    }

    if (firstErrorField?.name) {
      form.scrollToField(firstErrorField.name, { block: 'center' })
    }
  }

  useEffect(() => {
    if (data && Object.keys(data).length) {
      form.setFieldsValue(data)
    } else {
      form.resetFields()
    }
  }, [data])

  const onFinish = async (values: Values) => {
    try {
      submitLoadingActions.setTrue()
      
      const transformedValues = { ...values };
      
      formData.forEach((item) => {
        if ((item.type === FormItemType.Upload || item.type === FormItemType.File) && transformedValues[item.key]) {
          const fileList = transformedValues[item.key];
          
          if (Array.isArray(fileList)) {
            const urls = fileList
              .filter((file: any) => file.status === 'done' && file.response?.data?.url)
              .map((file: any) => file.response.data.url);
            
            if (item.type === FormItemType.File) {
              transformedValues[item.key] = urls.length > 0 ? urls[0] : null;
            } else if (item.type === FormItemType.Upload) {
              if (item.multiple === false) {
                transformedValues[item.key] = urls.length > 0 ? urls[0] : null;
              } else {
                transformedValues[item.key] = urls;
              }
            }
          }
        }
      });

      //@ts-ignore
      if (!!submitPath && !!submitMethod) {
        const requestBody = {
          body: {...transformedValues},
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
    
        switch (submitMethod) {
          case 'POST':
            const respPost = await post(submitPath, requestBody)
            if (respPost.ok) {
              submitSuccess?.(transformedValues)
            } else {
              const error = await respPost.response.json()
              message.error(error.message)
            }
            break
          case 'PUT':
            const respPut = await put(submitPath, requestBody)
            if (respPut.ok) {
              submitSuccess?.(transformedValues)
            } else {
              const error = await respPut.response.json()
              message.error(error.message)
            }
            break
          case 'PATCH':
            const respPatch = await patch(submitPath, requestBody)
            if (respPatch.ok) {
              submitSuccess?.(transformedValues)
            } else {
              const error = await respPatch.response.json()
              message.error(error.message)
            }
            break
          case 'DELETE':
            const respDelete = await destroy(submitPath, requestBody)
            if (respDelete.ok) {
              submitSuccess?.(transformedValues)
            } else {
              const error = await respDelete.response.json()
              message.error(error.message)
            }
            break
        }
      } else if (!!submit) {
        await submit?.(transformedValues)
      } else {
        return Promise.reject("submit error: no submitPath or onSubmit")
      }
    } catch (e) {
      console.log(e)
    } finally {
      submitLoadingActions.setFalse()
    }
  }

  return (
    <Card
      className={card ? 'mb-5' : 'ant-card-none'} title={title}
    >
      {loading ? (
        <Skeleton active />
      ) : (
        <AntForm<Values>
          layout="vertical"
          {...formProps}
          form={form}
          autoComplete="off"
          scrollToFirstError
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="antdForm"
        >
          <div className={wrapperClassNames}>
            {formData.map((item, index) => {
              return (<FormItem index={index} item={item} />)
            })}
          </div>
          {showActionButtons && (
            <AntForm.Item key="submit">
              <div className="flex space-x-3">
                <Button type="primary" htmlType="submit" loading={submitLoading} key="submit" onClick={(e) => {
                  e.stopPropagation()
                }}>
                  提交
                </Button>
                <Button
                  key="cancel"
                  onClick={(e) => {
                    e.stopPropagation()
                    cancel ? cancel() : window.history.back()
                  }}
                  disabled={submitLoading}
                >
                  取消
                </Button>
              </div>
            </AntForm.Item>
          )}
        </AntForm>
      )}
    </Card>
  )
}

const forwardFrom = React.forwardRef(Form)

export default registerComponentProperty<typeof forwardFrom, typeof AntForm>(forwardFrom, AntForm)
